import React from 'react';
import { 
  Maximize2, Activity, ShieldAlert, Layers, 
  Binary, Fingerprint, Gauge, Target 
} from 'lucide-react';

const EvaluationLogicSection = () => {
  return (
    <section className="space-y-12">
      {/* Header for the section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-800 pb-8">
        <div className="max-w-2xl">
          <h2 className="text-[10px] font-black uppercase tracking-[0.5em] text-indigo-500 mb-4">Core Engine Intelligence</h2>
          <h3 className="text-4xl font-black uppercase tracking-tighter">Diagnostic Decision Matrix</h3>
        </div>
        <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Manual Override: Enabled</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* LEFT COLUMN: THE THREE CORE FILTERS */}
        <div className="lg:col-span-8 space-y-4">
          {[
            {
              title: "Volumetric Verification",
              subtitle: "Geometric Echo Suppression",
              icon: <Layers size={20} />,
              content: "The engine cross-references the thickness of the material (WT) against the signal depth. It automatically ignores 'Root Echoes' or 'Cap Reflections' that often cause false positives in manual reporting.",
              tags: ["Wall Thickness Sync", "Geometry Masking"]
            },
            {
              title: "Amplitude Thresholding",
              subtitle: "Sensitivity Calibration (dB)",
              icon: <Gauge size={20} />,
              content: "Signals are evaluated against the DAC/TCG curve. If a signal exceeds 20% of the reference level, the engine triggers a 'Point of Interest' (POI) for automated sizing analysis.",
              tags: ["FSH Analysis", "Sensitivity Lock"]
            },
            {
              title: "Pattern Recognition",
              subtitle: "Flaw Characterization",
              icon: <Fingerprint size={20} />,
              content: "By analyzing the signal shape (envelope), the engine distinguishes between Volumetric Flaws (Slag/Porosity) and Planar Flaws (Cracks/Lack of Fusion) based on historical signal morphologies.",
              tags: ["Morphology Check", "Planar vs Volumetric"]
            }
          ].map((item, i) => (
            <div key={i} className="group bg-slate-900/50 border border-slate-800 p-8 rounded-[2rem] hover:bg-indigo-600/5 transition-all flex flex-col md:flex-row gap-8">
              <div className="w-14 h-14 shrink-0 bg-slate-800 rounded-2xl flex items-center justify-center text-indigo-400 group-hover:text-white group-hover:bg-indigo-600 transition-all">
                {item.icon}
              </div>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {item.tags.map(tag => (
                    <span key={tag} className="text-[8px] font-black uppercase tracking-tighter px-2 py-0.5 bg-slate-800 text-slate-500 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
                <h4 className="text-xl font-black uppercase italic tracking-tight">{item.title}</h4>
                <p className="text-sm text-slate-400 leading-relaxed font-medium">{item.content}</p>
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT COLUMN: REAL-TIME LOGIC STATUS */}
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-indigo-600 rounded-[2.5rem] p-8 text-white relative overflow-hidden h-full flex flex-col">
            <Binary className="absolute top-[-10%] right-[-10%] w-48 h-48 opacity-10 rotate-12" />
            
            <div className="relative z-10 space-y-6 flex-grow">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-200 italic">Auto-Logic Status</h4>
              <div className="space-y-6 pt-4">
                <div className="flex items-start gap-4">
                  <div className="w-1 h-12 bg-white/20 rounded-full" />
                  <div>
                    <p className="text-[10px] font-black uppercase text-indigo-100">Scan Input</p>
                    <p className="text-xl font-black italic tracking-tighter uppercase">Verified 1:1</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-12 bg-white/20 rounded-full">
                    <div className="w-full h-2/3 bg-white" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase text-indigo-100">Indication Parsing</p>
                    <p className="text-xl font-black italic tracking-tighter uppercase">94% Automated</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-10 pt-8 border-t border-white/10 mt-auto">
              <p className="text-[11px] font-bold leading-snug uppercase italic opacity-80">
                "The engine reduces decision fatigue by pre-filtering 90% of geometric noise."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EvaluationLogicSection;